import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import type { Role } from "@/modules/roles";
import { RoleSummary } from "./role-summary";
import styles from "./delete-role.module.scss";

export const ROLE_IN_USE_TITLE = "Não é possível excluir o perfil";
export const ROLE_IN_USE_DESCRIPTION = "Existem usuários vinculados a este perfil.";
export const ROLE_IN_USE_GUIDANCE =
  "Para excluir este perfil, remova os usuários vinculados ou altere o perfil deles na aba Usuários.";
export const LINKED_USERS_TITLE = "Usuários vinculados";
export const LINKED_USERS_ACTION = "Ir para a aba Usuários";

interface RoleInUseDialogProps {
  role: Role;
  onClose: () => void;
  onGoToUsers?: () => void;
}

function linkedUsersLabel(count: number): string {
  return count === 1 ? "usuário vinculado a este perfil" : "usuários vinculados a este perfil";
}

export function RoleInUseDialog({ role, onClose, onGoToUsers }: RoleInUseDialogProps) {
  const linkedUsersCount = role.quantidadeUsuarios;

  const header = (
    <span className={styles.header}>
      <span className={`${styles.headerIcon} ${styles.headerIconAlert}`} aria-hidden>
        <i className="pi pi-exclamation-triangle" />
      </span>

      <span className={styles.headerText}>
        <span className={styles.headerTitle}>{ROLE_IN_USE_TITLE}</span>
        <span className={styles.headerDescription}>{ROLE_IN_USE_DESCRIPTION}</span>
      </span>
    </span>
  );

  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Fechar"
        severity="secondary"
        text
        onClick={onClose}
        data-testid="role-in-use-close"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      header={header}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      ariaCloseIconLabel="Fechar"
      onHide={onClose}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="role-in-use-dialog"
    >
      <RoleSummary role={role} testId="role-in-use-summary" />

      <section
        className={styles.linkedUsersSection}
        aria-labelledby="linked-users-title"
        data-testid="role-in-use-users"
      >
        <h3 id="linked-users-title" className={styles.summaryTitle}>
          {LINKED_USERS_TITLE}
        </h3>

        <div className={styles.linkedUsersCard}>
          <div className={styles.usersCount}>
            <strong>{linkedUsersCount}</strong>
            <span>{linkedUsersLabel(linkedUsersCount)}</span>
          </div>

          <Button
            type="button"
            severity="secondary"
            label={LINKED_USERS_ACTION}
            icon="pi pi-arrow-right"
            iconPos="right"
            className={styles.usersAction}
            text
            onClick={onGoToUsers}
            disabled={!onGoToUsers}
            data-testid="role-in-use-go-to-users"
          />
        </div>
      </section>

      <div className={styles.info} role="note" data-testid="role-in-use-guidance">
        <i className={`pi pi-info-circle ${styles.infoIcon}`} aria-hidden />
        <p className={styles.infoText}>{ROLE_IN_USE_GUIDANCE}</p>
      </div>
    </Dialog>
  );
}
